/**
 * WebSocket connection manager with auto-reconnection.
 *
 * Reads the idle context internally so any caller (Options page,
 * AmbientTicker) automatically pauses its socket after 15 min of
 * no user input (or on tab-hidden) without needing a `paused` prop.
 * When `idle` flips back to false (user returns), the effect re-runs
 * and a fresh socket connects.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { HeatmapFrame } from '../lib/types';
import { WS_BASE_URL, RECONNECT_DELAYS } from '../lib/constants';
import { useIdle } from '../contexts/IdleContext';

interface UseWebSocketReturn {
  latestFrame: HeatmapFrame | null;
  frames: HeatmapFrame[];
  isConnected: boolean;
  lastUpdateTime: Date | null;
  switchTicker: (ticker: string) => void;
  error: string | null;
}

export function useWebSocket(ticker: string, maxFrames: number = 120): UseWebSocketReturn {
  const [latestFrame, setLatestFrame] = useState<HeatmapFrame | null>(null);
  const [frames, setFrames] = useState<HeatmapFrame[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { idle } = useIdle();

  // Mutable ref so switchTicker can reset the counter without closing
  // over a stale reconnectAttempt local variable from the effect closure.
  const reconnectAttemptRef = useRef(0);

  const switchTicker = useCallback((_newTicker: string) => {
    setFrames([]);
    setLatestFrame(null);
    // Reset reconnect backoff so the next connect attempt is immediate
    // (attempt 0 → RECONNECT_DELAYS[0] = 1000 ms, but the effect
    // re-fires synchronously on ticker change so the first connect is
    // actually instant; this reset matters for the *next* failure after
    // the switch, not the switch itself).
    reconnectAttemptRef.current = 0;
    // Reconnect happens via useEffect dep on `ticker`
  }, []);

  useEffect(() => {
    // Idle: don't open a socket. When idle flips back to false, this
    // effect re-runs and the connect block below fires fresh. Cleanup
    // closes any existing socket so we drop the backend's active set.
    if (idle) {
      setIsConnected(false);
      console.log('[GV] WebSocket paused (idle)');
      return;
    }

    let ws: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    // Use the shared ref so ticker switches reset the backoff counter
    // even while a reconnect timeout is in flight.
    reconnectAttemptRef.current = 0;

    function connect() {
      if (cancelled) return;

      const url = `${WS_BASE_URL}/${ticker}`;
      // Auth rides on the httpOnly session cookie, which the browser
      // attaches automatically to the WS upgrade handshake (same-site
      // request to api.gammavision.app). No subprotocol token any more —
      // the backend reads the cookie (see backend/api/ws.py::_ws_authed).
      console.log(`[GV] WebSocket connecting to ${url}`);

      try {
        ws = new WebSocket(url);

        ws.onopen = () => {
          if (cancelled) { ws?.close(); return; }
          console.log(`[GV] WebSocket connected`);
          setIsConnected(true);
          setError(null);
          reconnectAttemptRef.current = 0;
        };

        ws.onmessage = (event) => {
          if (cancelled) return;
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'ping' || data.type === 'pong') return;
            if (data.type === 'event' && data.event) {
              // Server-pushed state-change event (king moved, flip moved,
              // air pocket…). Re-broadcast on the window bus so any page
              // (Dashboard's feed) can consume pushes without holding its
              // own socket — AmbientTicker's socket is on every authed
              // page. MUST return before the frame cast below.
              window.dispatchEvent(new CustomEvent('gv:event', { detail: data.event }));
              return;
            }

            const frame = data as HeatmapFrame;
            setLatestFrame(frame);
            setLastUpdateTime(new Date());
            setFrames(prev => {
              const updated = [...prev, frame];
              return updated.slice(-maxFrames);
            });
          } catch {
            // Ignore parse errors
          }
        };

        ws.onclose = (event) => {
          if (cancelled) return;
          setIsConnected(false);
          // Terminal close codes from the backend (backend/api/ws.py):
          //   4401 — auth required (missing / invalid session cookie)
          //   4403 — tier does not permit this ticker (GV-01 paywall gate)
          //   4004 — unknown / inadmissible ticker
          // Reconnecting can NEVER succeed for these (the session, tier,
          // or symbol won't change by retrying), so we stop and surface an
          // error instead of re-running the handshake every 30s forever
          // (which a free-tier user's ambient SPX socket, or an Essentials
          // user opening a Pro-only ticker, would otherwise do). Transient
          // closes (network blips, code 1006, idle-proxy close) fall through
          // to the normal exponential-backoff reconnect.
          if (event.code === 4401 || event.code === 4403 || event.code === 4004) {
            console.log(`[GV] WebSocket closed (terminal ${event.code}: ${event.reason}); not reconnecting`);
            setError(
              event.code === 4403
                ? 'Your plan does not include live data for this ticker.'
                : event.code === 4401
                ? 'Please sign in to view live data.'
                : 'This ticker is not available.'
            );
            return;
          }
          console.log(`[GV] WebSocket closed (code ${event.code}); reconnecting`);
          scheduleReconnect();
        };

        ws.onerror = () => {
          if (cancelled) return;
          setError('WebSocket connection error');
          // onclose fires after this
        };
      } catch (err) {
        setError(`Failed to connect: ${err}`);
        scheduleReconnect();
      }
    }

    function scheduleReconnect() {
      if (cancelled) return;
      const attempt = reconnectAttemptRef.current;
      const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
      console.log(`[GV] Reconnecting in ${delay}ms (attempt ${attempt + 1})`);
      reconnectAttemptRef.current = attempt + 1;
      reconnectTimeout = setTimeout(connect, delay);
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.close();
      }
      setIsConnected(false);
    };
  }, [ticker, maxFrames, idle]);

  return {
    latestFrame,
    frames,
    isConnected,
    lastUpdateTime,
    switchTicker,
    error,
  };
}
